<div class="po-component-container">
  <!-- dashboard.component.html -->
  <div class="page scrollbar">
    <div class="page-header">
      <h2>Restaurant Dashboard</h2>
      <div class="header-actions">
        <button nz-button nzType="default" nzShape="circle" nz-tooltip nzTooltipTitle="Refresh" (click)="onRefresh()">
          <span nz-icon nzType="reload"></span>
        </button>
        <nz-tag nzColor="green">Live</nz-tag>
      </div>
    </div>

    <!-- Top stat cards -->
    <div nz-row [nzGutter]="16" class="mb-3">
      <div nz-col [nzXs]="24" [nzSm]="12" [nzMd]="6" *ngIf="canViewSales">
        <nz-card class="stat-card">
          <div class="stat-title">Today's Sales</div>
          <div class="stat-value">{{ todaySales | currency: 'Rs. ' }}</div>
          <span class="stat-sub">{{ todayBills | number }} bill(s) today</span>
        </nz-card>
      </div>

      <div nz-col [nzXs]="24" [nzSm]="12" [nzMd]="6">
        <nz-card class="stat-card">
          <div class="stat-title">Available Tables</div>
          <div class="stat-row">
            <div class="stat-value text-success">{{ tableSummary.available }} / {{ tableSummary.total }}</div>
            <span class="dot dot-green"></span>
          </div>
          <span class="stat-sub">Ready for guests of {{ tableSummary.total }} capacity</span>
        </nz-card>
      </div>

      <div nz-col [nzXs]="24" [nzSm]="12" [nzMd]="6">
        <nz-card class="stat-card">
          <div class="stat-title">Occupied Tables</div>
          <div class="stat-row">
            <div class="stat-value text-orange">{{ tableSummary.occupied }}</div>
            <span class="dot dot-orange"></span>
          </div>
          <span class="stat-sub">{{ (tableSummary.occupied / tableSummary.total * 100) | number:'1.0-0' }}% occupancy rate</span>
        </nz-card>
      </div>

      <div nz-col [nzXs]="24" [nzSm]="12" [nzMd]="6">
        <nz-card class="stat-card">
          <div class="stat-title">Ongoing Orders</div>
          <div class="stat-value">{{ orderSummary.ongoing }}</div>
          <span class="stat-sub">In progress</span>
        </nz-card>
      </div>
    </div>

    <!-- Middle section: Table Management + Order Management -->
    <div nz-row [nzGutter]="16" class="mb-3">
      <div nz-col [nzXs]="24" [nzMd]="12" class="mb-3">
        <nz-card [nzTitle]="tmTitle">
          <ng-template #tmTitle>
            <div class="card-title">
              <span nz-icon nzType="table"></span>
              <span>Table Management</span>
            </div>
            <div class="card-sub">Current table status and availability</div>
          </ng-template>

          <div class="tm-wrapper">
            <div class="donut">
              <canvas #tableMgmtDonut></canvas>
              <div class="donut-center">
                <div class="donut-value">{{ tableSummary.total }}</div>
                <div class="donut-label">Total Tables</div>
              </div>
            </div>

            <div class="legend">
              <div class="legend-row">
                <span class="dot dot-green"></span>
                <span>Available ({{ tableSummary.available }})</span>
              </div>
              <div class="legend-row">
                <span class="dot dot-orange"></span>
                <span>Occupied ({{ tableSummary.occupied }})</span>
              </div>
            </div>
          </div>

          <div class="tm-cards">
            <div class="tm-card available">
              <div class="tm-card-value">{{ tableSummary.available }}</div>
              <div class="tm-card-label">Available</div>
            </div>
            <div class="tm-card occupied">
              <div class="tm-card-value">{{ tableSummary.occupied }}</div>
              <div class="tm-card-label">Occupied</div>
            </div>
          </div>
        </nz-card>
      </div>

      <div nz-col [nzXs]="24" [nzMd]="12" class="mb-3">
        <nz-card [nzTitle]="omTitle">
          <ng-template #omTitle>
            <div class="card-title">
              <span nz-icon nzType="ordered-list"></span>
              <span>Order Management</span>
            </div>
            <div class="card-sub">Order status and completion tracking</div>
          </ng-template>

          <div class="om-stats">
            <div class="om-row">
              <span>Total Orders</span>
              <strong>{{ orderSummary.total }}</strong>
            </div>
            <div class="om-row">
              <span>Ongoing Orders</span>
              <strong>{{ orderSummary.ongoing }}</strong>
            </div>
            <div class="om-row">
              <span>Completed Orders</span>
              <strong>{{ orderSummary.completed }}</strong>
            </div>
            <div class="om-progress">
              <span>Completion Rate</span>
              <nz-progress [nzPercent]="orderSummary.completionRate" nzStatus="active" [nzStrokeWidth]="10"></nz-progress>
            </div>
          </div>
        </nz-card>
      </div>
    </div>

    <!-- Bottom section: Table Status + Recent Orders -->
    <div nz-row [nzGutter]="16">
      <div nz-col [nzXs]="24" [nzMd]="12">
        <nz-card nzTitle="Table Status">
          <div class="table-list">
            @for (t of tables; track t) {
              <div class="table-item">
                <div class="left">
                  <span class="status-dot" [class.green]="t.availabilityStatus === 1" [class.orange]="t.availabilityStatus !== 1"></span>
                  <strong>{{ t.name }}</strong>
                  @if (t.availabilityStatus !== 1) {
                    <span class="muted">
                      • {{ t.seatingCapacity }} guests •
                    </span>
                  }
                </div>
                <div class="right">
                  @if (t.availabilityStatus === 1) {
                    <nz-tag nzColor="success">Available</nz-tag>
                  }
                  @if (t.availabilityStatus !== 1) {
                    <nz-tag nzColor="orange">Occupied</nz-tag>
                  }
                </div>
              </div>
            }
          </div>
        </nz-card>
      </div>

      <div nz-col [nzXs]="24" [nzMd]="12">
        <nz-card nzTitle="Recent Orders">
          <nz-list nzItemLayout="horizontal">
            @for (order of recentOrders; track order) {
              <nz-list-item>
                <nz-list-item-meta
                  [nzTitle]="order.id"
                  [nzDescription]="order.minutesAgo + ' min ago'">
                </nz-list-item-meta>
                @if (order.status==='In Progress') {
                  <nz-tag nzColor="blue">In Progress</nz-tag>
                }
                @if (order.status==='Completed') {
                  <nz-tag nzColor="green">Completed</nz-tag>
                }
              </nz-list-item>
            }
          </nz-list>

        </nz-card>
      </div>
    </div>
  </div>

</div>
